import type { ReactNode } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { DropdownMenu } from "radix-ui";
import { Check, ChevronsUpDown, LayoutGrid, LogOut, UserRound } from "lucide-react";
import { useSession } from "../lib/session.tsx";
import { get } from "../lib/api.ts";
import { useGuardedNavigate } from "../lib/guard.tsx";
import { Brand, Credits } from "./brand.tsx";
import { roleLabel, type PlansResponse } from "../lib/types.ts";

export function SkipLink() {
  return (
    <a href="#conteudo" className="sr-only rounded-ctl bg-primary px-4 py-2 font-semibold text-white focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70]">
      Pular para o conteúdo
    </a>
  );
}

/** Anonymous pages: centered card, no navigation. */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <SkipLink />
      <header className="bg-brand px-4 py-2.5 sm:px-6">
        <Brand href="/login" />
      </header>
      <main id="conteudo" className="flex flex-1 items-start justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-md">{children}</div>
      </main>
      <Credits className="px-4 pb-6" />
    </div>
  );
}

/** Quick plan switcher (UX-07): current plan prominent, every accessible plan one click away. */
export function PlanSwitcher({ currentId, currentName }: { currentId: string; currentName: string }) {
  const go = useGuardedNavigate();
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => get<PlansResponse>("/plans?page_size=100") });
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" className="tap flex min-w-0 max-w-full items-center gap-1.5 rounded-ctl px-2 py-1 text-left text-white hover:bg-white/10 data-[state=open]:bg-white/10" aria-label={`Oficina atual: ${currentName}. Trocar oficina`}>
          <span className="truncate text-sm font-semibold">{currentName}</span>
          <ChevronsUpDown className="size-4 shrink-0 text-white/70" aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={6} className="z-50 w-[min(22rem,calc(100vw-2rem))] rounded-panel border border-line bg-surface p-1.5 shadow-e3 animate-scale-in">
          <DropdownMenu.Label className="px-3 py-1.5 text-xs font-semibold text-muted">Minhas oficinas</DropdownMenu.Label>
          {(plans.data?.data ?? []).map((p) => (
            <DropdownMenu.Item key={p.id} onSelect={() => go(p.access_state === "expired" ? `/planos/${p.id}/acesso-encerrado` : `/planos/${p.id}/painel`)}
              className="flex min-h-10 items-start gap-2 rounded-ctl px-3 py-2 text-sm outline-none data-[highlighted]:bg-paper">
              <Check className={`mt-0.5 size-4 shrink-0 ${p.id === currentId ? "text-primary" : "invisible"}`} aria-hidden />
              <span className="min-w-0">
                <span className="block truncate font-medium">{p.name}</span>
                <span className="block truncate text-xs text-muted">{p.institution.name} · {roleLabel[p.role]}{p.access_state === "expired" ? " · acesso encerrado" : ""}</span>
              </span>
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item onSelect={() => go("/planos")} className="flex min-h-10 items-center gap-2 rounded-ctl px-3 text-sm outline-none data-[highlighted]:bg-paper">
            <LayoutGrid className="size-4 text-muted" aria-hidden /> Ver todas as oficinas
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Authenticated top bar: brand (+ plan switcher inside a plan) on the left, account on the right. */
export function TopBar({ plan }: { plan?: { id: string; name: string } }) {
  const { user, logout } = useSession();
  return (
    <header className="sticky top-0 z-30 flex min-h-16 items-center justify-between gap-3 bg-brand bg-[linear-gradient(180deg,#123358_0%,#0d2845_100%)] px-3 py-2 text-white shadow-[0_1px_0_rgb(255_255_255/0.06)_inset,0_6px_18px_-12px_rgb(13_40_69/0.6)] sm:px-5">
      <div className="flex min-w-0 items-center gap-2">
        <Brand compact={Boolean(plan)} />
        {plan && (
          <>
            <span className="text-white/30" aria-hidden>/</span>
            <PlanSwitcher currentId={plan.id} currentName={plan.name} />
          </>
        )}
      </div>
      <nav aria-label="Conta" className="flex shrink-0 items-center gap-1">
        <Link href="/perfil" className="tap inline-flex items-center gap-1.5 rounded-ctl px-2.5 py-2 text-sm text-white/90 transition-colors duration-[var(--duration-base)] hover:bg-white/10 hover:text-white active:bg-white/15">
          <UserRound className="size-4" aria-hidden />
          <span className="hidden max-w-40 truncate sm:inline">{user?.name}</span>
          <span className="sm:hidden">Perfil</span>
        </Link>
        <button type="button" onClick={() => void logout()} className="tap inline-flex items-center gap-1.5 rounded-ctl px-2.5 py-2 text-sm text-white/90 transition-colors duration-[var(--duration-base)] hover:bg-white/10 hover:text-white active:bg-white/15">
          <LogOut className="size-4" aria-hidden />
          Sair
        </button>
      </nav>
    </header>
  );
}

export function AccountLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh">
      <SkipLink />
      <TopBar />
      <main id="conteudo" className="mx-auto max-w-5xl px-4 py-8 sm:px-6">{children}</main>
      <Credits className="px-4 pb-6" />
    </div>
  );
}
